import ThreeDotsOverlay from "@/shared/components/generic/loading/ThreeDotsOverlay";
import { AppUserInfoContext } from "@/shared/model/user/AppUserInfoContext";
import { goToLoginWithTarget } from "@/shared/model/user/Logout";
import {
  buildAppUserInfoFromLocalStorage,
  buildAppUserInfoFromObject,
} from "@/shared/model/user/js-cache/UserJsCache.ts";
import { buildFrontendStateFromLocalStorage } from "@/shared/model/user/js-cache/FrontJsCache";
import "@/shared/style/design_system/Fonts.scss";
import { handleError, handleSuccess } from "@/shared/utils/AxiosUtils";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import axios from "axios";
import { Suspense, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import "./i18n";

import { AppUserInfo } from "@/shared/model/user/AppUserInfo";
import routes from "./routes";
import {
  enUS as muiEnglish,
  frFR as muiFrench,
} from "@mui/x-date-pickers/locales";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { ThreeDotsOverlayProvider } from "@/shared/components/generic/loading/ThreeDotsOverlayProvider.tsx";
import GlobalThreeDotsOverlay from "@/shared/components/generic/loading/GlobalThreeDotsOverlay.tsx";
import { FrontendStateContext } from "@/shared/model/user/js-cache/FrontendStateContext";
import { AppUserInfoUpdateContext } from "@/shared/model/user/AppUserInfoUpdateContext.ts";
import useOnBroadcastAction from "@/shared/services/broadcast/useOnBroadcastAction.ts";

import { useConfiguredQueryClient } from "@/shared/core/error/useConfiguredQueryClient";
import { Toaster } from "react-hot-toast";
// Router is static, hence built once and for all, *not* inside App component.
const router = createBrowserRouter(routes());

axios.interceptors.response.use(handleSuccess, handleError);

export default function App(): React.ReactElement {
  if (import.meta.env.DEV) {
    console.debug("### Rendering App ###");
  }

  const [appUserInfo, setAppUserInfo] = useState(
    buildAppUserInfoFromLocalStorage(),
  );

  const [frontendState, setFrontendState] = useState(
    buildFrontendStateFromLocalStorage(),
  );
  void setFrontendState;

  const { t, i18n } = useTranslation();

  useEffect(() => {
    const userLanguage = appUserInfo.userInfo?.language;
    const currentLanguage = i18n.language.substring(0, 2).toLowerCase();
    if (userLanguage != undefined && userLanguage !== currentLanguage) {
      void i18n.changeLanguage(userLanguage);
    }
  }, [i18n, appUserInfo.userInfo?.language]);

  useOnBroadcastAction({
    onFrontendStateUpdated: () => {
      setFrontendState(buildFrontendStateFromLocalStorage());
    },
    onBackendDataRefreshed: (payload) => {
      setAppUserInfo(buildAppUserInfoFromObject(payload));
    },
    onLogout: () => {
      setAppUserInfo(new AppUserInfo(false));
    },
  });

  const queryClient = useConfiguredQueryClient();

  if (import.meta.env.DEV) {
    console.debug("### Rendering App ###");
  }

  const getMuiLocaleText = useCallback(() => {
    const currentLanguage = i18n.language.substring(0, 2).toLowerCase();
    if (currentLanguage === "fr") {
      return muiFrench.components.MuiLocalizationProvider.defaultProps
        .localeText;
    } else if (currentLanguage === "en") {
      return muiEnglish.components.MuiLocalizationProvider.defaultProps
        .localeText;
    }
    console.error(`"Unsupported MuiLocaleText language: ${currentLanguage}`);
  }, [i18n.language]);

  if (!appUserInfo.loggedIn) {
    goToLoginWithTarget();
  } else {
    const target = new URLSearchParams(window.location.search).get("target");
    if (target != null) {
      return <Navigate to={target} replace />;
    }
  }

  const appContent = (
    <>
      <Toaster position="bottom-left" />
      <LocalizationProvider
        dateAdapter={AdapterLuxon}
        adapterLocale={appUserInfo.userInfo?.language}
        localeText={getMuiLocaleText()}
      >
        <QueryClientProvider client={queryClient}>
          <AppUserInfoContext.Provider value={appUserInfo}>
            <AppUserInfoUpdateContext.Provider value={setAppUserInfo}>
              <FrontendStateContext.Provider value={frontendState}>
                <ThreeDotsOverlayProvider>
                  <RouterProvider router={router} />
                  <GlobalThreeDotsOverlay />
                  <ReactQueryDevtools
                    initialIsOpen={false}
                    buttonPosition="bottom-right"
                  />
                </ThreeDotsOverlayProvider>
              </FrontendStateContext.Provider>
            </AppUserInfoUpdateContext.Provider>
          </AppUserInfoContext.Provider>
        </QueryClientProvider>
      </LocalizationProvider>
    </>
  );

  const loginLink = <a href="/">{t("common:messages.pleaseReconnect")}</a>;

  return (
    <Suspense fallback={<ThreeDotsOverlay />}>
      <div className="body medium">
        {appUserInfo.loggedIn ? appContent : loginLink}
      </div>
    </Suspense>
  );
}
